/**
 * `/account` — the signed-in member's own account on saas-auth `/api/account/**` (H §4.6):
 * name, password, two-step sign-in and language. Any role may use it — it is the member's
 * own account, not a reseller write. Devices, sessions and connected apps are not available to
 * resellers and are never shown.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountApi, type AccountMfa, type AccountProfile } from '@/api/account';
import { ApiErrorState } from '@/components/ApiErrorState';
import { PageHeader } from '@/components/PageHeader';
import { useUnsavedGuard } from '@/components/useUnsavedGuard';
import { LanguageSection } from './LanguageSection';
import { MfaSection } from './MfaSection';
import { PasswordSection } from './PasswordSection';
import { ProfileSection } from './ProfileSection';

type Part<T> =
  | { kind: 'pending' }
  | { kind: 'failed'; error: unknown }
  | { kind: 'ready'; data: T };

export function AccountScreen() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<Part<AccountProfile>>({ kind: 'pending' });
  const [mfa, setMfa] = useState<Part<AccountMfa>>({ kind: 'pending' });
  const started = useRef(false);
  // One guard for the page (React Router supports one blocker at a time): either section edited.
  const [profileDirty, setProfileDirty] = useState(false);
  const [passwordDirty, setPasswordDirty] = useState(false);
  const guard = useUnsavedGuard({
    when: profileDirty || passwordDirty,
    name: t('unsaved.names.account'),
  });

  const loadProfile = useCallback(() => {
    setProfile({ kind: 'pending' });
    accountApi.profile().then(
      (data) => setProfile({ kind: 'ready', data }),
      (error: unknown) => setProfile({ kind: 'failed', error }),
    );
  }, []);

  const loadMfa = useCallback(() => {
    setMfa({ kind: 'pending' });
    accountApi.mfa().then(
      (data) => setMfa({ kind: 'ready', data }),
      (error: unknown) => setMfa({ kind: 'failed', error }),
    );
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    loadProfile();
    loadMfa();
  }, [loadProfile, loadMfa]);

  return (
    <Box>
      <PageHeader id="account" />
      <Stack spacing={3}>
        {profile.kind === 'pending' ? (
          <Box role="status" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress aria-label={t('common.loading')} />
          </Box>
        ) : null}
        {profile.kind === 'failed' ? (
          <ApiErrorState what={t('errors.what.account')} error={profile.error} onRetry={loadProfile} />
        ) : null}
        {profile.kind === 'ready' ? (
          <>
            <ProfileSection
              profile={profile.data}
              onSaved={(data) => setProfile({ kind: 'ready', data })}
              onDirtyChange={setProfileDirty}
            />
            <PasswordSection hasPassword={profile.data.hasPassword} onDirtyChange={setPasswordDirty} />
          </>
        ) : null}
        {mfa.kind === 'pending' ? (
          <Box role="status" sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} aria-label={t('common.loading')} />
          </Box>
        ) : null}
        {mfa.kind === 'failed' ? <ApiErrorState error={mfa.error} onRetry={loadMfa} /> : null}
        {mfa.kind === 'ready' ? (
          <MfaSection mfa={mfa.data} onChanged={(data) => setMfa({ kind: 'ready', data })} />
        ) : null}
        <LanguageSection />
      </Stack>
      {guard}
    </Box>
  );
}
